import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Lock, RefreshCw } from 'lucide-react';
import { type ModuleData, getModuleDisplayInfo, getModuleColors, getModuleIcon } from '../../content';
import { GroupId } from '../../config/systemConfig';
import { getGrowthStage } from '../../mastery';
import { GrowthStageIcon } from '../shared/GrowthStageIcon';
import { soundManager } from '../../utils/soundManager';
interface WorldTileProps {
  module: ModuleData;
  selectedGroup: GroupId;
  onClick: () => void;
  getModuleMasteryForGroup: (moduleId: string) => number;
  getAnsweredQuestions: (moduleId: string) => unknown[];
  index?: number;
  hasDecay?: boolean;
}
export function WorldTile({
  module,
  selectedGroup,
  onClick,
  getModuleMasteryForGroup,
  getAnsweredQuestions,
  index = 0,
  hasDecay = false
}: WorldTileProps) {
  const mastery = getModuleMasteryForGroup(module.id);
  const answered = getAnsweredQuestions(module.id).length;
  const isStarted = answered > 0 || mastery > 0;
  const isMastered = mastery >= 95;
  const colors = getModuleColors(module.color);
  const Icon = getModuleIcon(module.icon);
  const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
  const title = displayInfo?.title || module.title;
  const growthStage = getGrowthStage(mastery);
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8,
    y: 20
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} transition={{
    delay: index * 0.05,
    type: 'spring',
    bounce: 0.5
  }} whileHover={isStarted || !isStarted ? {
    y: -8,
    scale: 1.05
  } : {}} whileTap={{
    scale: 0.95
  }} className="w-full max-w-[170px] sm:max-w-[180px] cursor-pointer group relative" onClick={() => {
    soundManager.playNavigate();
    onClick();
  }} data-id="element-2479">
      {/* Decay Badge */}
      {hasDecay && !isMastered && <div className="absolute -top-2 -right-2 z-30" data-id="element-2480">
          <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} className="bg-teal-500 text-white rounded-full p-1.5 shadow-md border-2 border-white" title="Herhaling nodig" data-id="element-2481">
            <RefreshCw className="w-4 h-4" data-id="element-2482" />
          </motion.div>
        </div>}

      {/* Main Tile Container */}
      <div className={`relative flex flex-col items-center p-4 sm:p-5 rounded-[2rem] shadow-sm transition-shadow duration-300 group-hover:shadow-xl border-4 border-white
          ${isMastered ? 'bg-gradient-to-b from-emerald-300 to-emerald-500' : isStarted ? `bg-gradient-to-b ${colors.from} ${colors.to}` : 'bg-slate-100'}`} data-id="element-2483">
        {/* Top Badge (Emoji or Lock) - Hidden on mobile */}
        <div className="hidden sm:block absolute -top-4 left-1/2 -translate-x-1/2 z-20" data-id="element-2484">
          <motion.div initial={{
          y: 10,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} className={`drop-shadow-md bg-white rounded-full w-10 h-10 flex items-center justify-center border-4 ${isMastered ? 'border-emerald-100' : isStarted ? colors.border : 'border-slate-200'}`} data-id="element-2485">
            {isMastered ? <span className="text-2xl" data-id="element-2486">👑</span> : isStarted ? <GrowthStageIcon masteryScore={mastery} size="sm" data-id="element-2487" /> : <Lock className="w-4 h-4 text-slate-400" data-id="element-2488" />}
          </motion.div>
        </div>

        {/* Icon Circle */}
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-inner mb-2 sm:mb-3 mt-1 sm:mt-2 group-hover:scale-110 transition-transform duration-300" data-id="element-2489">
          <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${isMastered ? 'text-emerald-500' : isStarted ? colors.text : 'text-slate-400'}`} strokeWidth={2.5} data-id="element-2490" />
        </div>

        {/* Title */}
        <h3 className={`font-black text-center text-sm sm:text-base leading-tight mb-2 sm:mb-3 w-full line-clamp-2
          ${isMastered || isStarted ? 'text-white drop-shadow-sm' : 'text-slate-600'}`} data-id="element-2491">
          {title}
        </h3>

        {/* Progress Area */}
        <div className="w-full mt-auto" data-id="element-2492">
          {isMastered ? <div className="bg-white/20 rounded-full py-1.5 px-2 flex items-center justify-center gap-1 backdrop-blur-sm" data-id="element-2493">
              <CheckCircle2 className="w-4 h-4 text-white" data-id="element-2494" />
              <span className="text-xs font-black text-white" data-id="element-2495">100%</span>
            </div> : isStarted ? <div className="space-y-1.5" data-id="element-2496">
              <div className="flex justify-between items-center px-1" data-id="element-2497">
                <span className="text-[10px] font-bold text-white/90 uppercase tracking-wider" data-id="element-2498">
                  {growthStage.label}
                </span>
                <span className="text-[10px] font-black text-white" data-id="element-2499">
                  {Math.round(mastery)}%
                </span>
              </div>
              <div className="h-2.5 w-full bg-black/10 rounded-full overflow-hidden shadow-inner" data-id="element-2500">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: `${mastery}%`
            }} transition={{
              duration: 1,
              ease: 'easeOut',
              delay: 0.2
            }} className="h-full bg-white rounded-full" data-id="element-2501" />
              </div>
            </div> : <div className="bg-white/50 rounded-full py-1.5 px-2 flex items-center justify-center backdrop-blur-sm" data-id="element-2502">
              <span className="text-xs font-black text-slate-500 uppercase tracking-wider" data-id="element-2503">
                Nieuw
              </span>
            </div>}
        </div>
      </div>
    </motion.div>;
}